import allImgPaths from "assets/images";
import CustomSelect from "./common/CustomSelect";
import { useEffect, useState } from "react";

const CitationsSidebar = ({
  isShowSideBarCitations,
  setIsShowSideBarCitations,
  citationsData,
  selectedCitation,
  setSelectedCitation,
  partsListData = [],
  activeTab,
  onTabChange,
}: any) => {
  const [activeCitation, setActiveCitation] = useState<any>(0);
  const [citationAccordionData, setCitationAccordionData] = useState<any[]>([]);
  const [tabState, setTabState] = useState<"citations" | "parts">("citations");
  const [expandedPatents, setExpandedPatents] = useState<
    Record<string, boolean>
  >({});
  const tab = activeTab ?? tabState;
  const setTab = onTabChange ?? setTabState;
  const hasParts = Array.isArray(partsListData) && partsListData.length > 0;
  const showParts = hasParts && tab === "parts";

  const isPatentOpen = (pid: string) => !!expandedPatents[pid];
  const togglePatent = (pid: string) =>
    setExpandedPatents((prev) => ({ ...prev, [pid]: !isPatentOpen(pid) }));

  // Citation clicks always set a fresh selectedCitation — snap back to the Citations tab.
  useEffect(() => {
    if (selectedCitation) setTab("citations");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCitation]);

  const citationOptions = citationsData.map((citation: any) => ({
    value: citation.number,
    label: `${citation.number}`,
  }));
  const handleCitationClick = (index: any) => {
    index === 0 && setActiveCitation(index);
  };

  const handleCitationChange = (selectedOption: any) => {
    setSelectedCitation(selectedOption);
  };

  const formatPrimaryHeading = (data: any) => {
    const parts = [];

    // Paragraph number formatting (check first, as it's the preferred format)
    if (data.paragraph_start_number && data.paragraph_start_number !== "None") {
      const paragraphLabel =
        data.paragraph_end_number && data.paragraph_end_number !== "None" && data.paragraph_start_number !== data.paragraph_end_number
          ? `Paragraph ${data.paragraph_start_number}-${data.paragraph_end_number}`
          : `Paragraph ${data.paragraph_start_number}`;
      return `Specification at ${paragraphLabel}`;
    }

    // Page number formatting
    if (data.starting_page_number && data.starting_page_number !== "None") {
      const pageLabel =
        data.starting_page_number === data.ending_page_number
          ? `Page ${data.starting_page_number}`
          : `Pages ${data.starting_page_number}-${data.ending_page_number}`;
      parts.push(pageLabel);
    }

    // Column number formatting
    if (data.starting_column_number && data.starting_column_number !== "None") {
      const columnLabel =
        data.starting_column_number === data.ending_column_number
          ? `Column ${data.starting_column_number}`
          : `Columns ${data.starting_column_number}-${data.ending_column_number}`;
      parts.push(columnLabel);
    }

    // Line number formatting
    if (data.starting_line_number && data.starting_line_number !== "None") {
      const lineLabel =
        data.starting_line_number === data.ending_line_number
          ? `Line ${data.starting_line_number}`
          : `Lines ${data.starting_line_number}-${data.ending_line_number}`;
      parts.push(lineLabel);
    }

    if (parts.length > 0) {
      return `Specification at ${parts.join(", ")}`;
    }
    
    // Show the filename if there's a file_url and file_name but no specific location data
    if (data.file_url && data.file_name && !data.starting_line_number && !data.starting_page_number) {
      return data.file_name;
    }
    
    // Show the full URL if there's a url field, otherwise show "Specification"
    if (data.url && data.url.trim()) {
      return data.url;
    }
    return "Specification";
  };

  const formatFileUrl = (data: any) => {
    if (data.file_url && data.file_url.trim()) {
      return data.starting_page_number
        ? `${data.file_url}#page=${data.starting_page_number}`
        : data.file_url;
    }
    if (data.url && data.url.trim()) {
      return data.url;
    }
    return null;
  };

  useEffect(() => {
    if (!selectedCitation) return;

    const selectedCitationData =
      selectedCitation?.value &&
      citationsData.find(
        (citation: any) => citation.number === Number(selectedCitation.value)
      );

    if (selectedCitationData) {
      const primary_heading = formatPrimaryHeading(selectedCitationData);
      const file_url = formatFileUrl(selectedCitationData);
      const isUrlOnly = selectedCitationData.url && selectedCitationData.url.trim() && !selectedCitationData.file_url;
      const isFileWithoutLocation = selectedCitationData.file_url && selectedCitationData.file_name && !selectedCitationData.starting_line_number && !selectedCitationData.starting_page_number;
      const label = (isUrlOnly || isFileWithoutLocation) ? "References" : "Specification";
      
      const accordionItems = [
        {
          label,
          primary_heading,
          desc: selectedCitationData.description,
          file_url,
          has_url: !!selectedCitationData.url,
          has_file_url: !!selectedCitationData.file_url,
        }
      ];
      
      // Only add Prosecutional and Precedential sections if it's not a URL-only citation or file without location data
      if (!isUrlOnly && !isFileWithoutLocation) {
        accordionItems.push(
          {
            label: "Prosecutional",
            primary_heading: "",
            desc: "",
            file_url: null,
            has_url: false,
            has_file_url: false,
          },
          {
            label: "Precedential",
            primary_heading: "",
            desc: "",
            file_url: null,
            has_url: false,
            has_file_url: false,
          }
        );
      }
      
      setCitationAccordionData(accordionItems);
    } else {
      setCitationAccordionData([]);
    }
  }, [selectedCitation, citationsData]);

  return (
    <div
      className={`w-full h-full fixed bg-[#07031154] z-[100] ${
        isShowSideBarCitations ? "right-0" : "right-full"
      }`}
    >
      <div
        onClick={() => setIsShowSideBarCitations(false)}
        className="h-full w-[calc(100%_-_360px)]"
      ></div>
      <div
        id="citation-panel"
        className={`border-l border-[#E4E7EC] w-[360px] h-screen fixed z-50 bg-white top-0 shadow-customShadow15 ${
          isShowSideBarCitations ? "right-0" : "right-full"
        }`}
      >
        <div className="pb-4 px-5 pt-4">
          <div className="pb-0.5 flex items-center justify-between">
            <span className="text-lg font-interSemiBold text-[#101828] inline-block ">
              {showParts ? "Parts List" : "Citation"}
            </span>
            <img
              onClick={() => setIsShowSideBarCitations(false)}
              className="walkthrough-citation-close cursor-pointer hover:bg-[#f2f4f7] rounded w-5 p-1"
              src={allImgPaths.closeIcon}
              alt=""
            />
          </div>

          <p className="text-[#475467] text-sm ">
            {showParts
              ? "Part numbers grouped by patent"
              : "Reference to the source file"}
          </p>
        </div>

        {hasParts && (
          <div className="bg-[#F8F8F8] rounded-lg p-1.5 mx-5 mb-4">
            <div className="flex items-center gap-x-1">
              <p
                onClick={() => setTab("citations")}
                className={`text-sm font-interMedium rounded-lg py-1.5 px-2 flex-1 flex justify-center cursor-pointer ${
                  !showParts
                    ? "bg-white shadow-customShadow12 text-[#0A090B]"
                    : "text-[#4F4D55]"
                }`}
              >
                Citations
              </p>
              <p
                onClick={() => setTab("parts")}
                className={`text-sm font-interMedium rounded-lg py-1.5 px-2 flex-1 flex justify-center cursor-pointer ${
                  showParts
                    ? "bg-white shadow-customShadow12 text-[#0A090B]"
                    : "text-[#4F4D55]"
                }`}
              >
                Parts List
              </p>
            </div>
          </div>
        )}

        {showParts && (
          <div className="overflow-y-auto px-5 pb-4 h-[calc(100vh_-_150px)]">
            {partsListData.map((p: any) => (
              <div className="mb-2 last:mb-0" key={p.patentId}>
                <div
                  className="bg-[#F2F5FF] rounded-sm py-2 pl-2 pr-3 flex items-center justify-between cursor-pointer"
                  onClick={() => togglePatent(p.patentId)}
                >
                  <span className="text-sm font-interMedium text-[#101828]">
                    {p.patentId}
                  </span>
                  <div className="flex items-center gap-x-2">
                    <span className="text-xs text-[#475467]">
                      {p.parts.length} parts
                    </span>
                    <img
                      className={`cursor-pointer hover:bg-[#f2f4f7] rounded w-5 transition-transform ${
                        isPatentOpen(p.patentId) ? "" : "rotate-90"
                      }`}
                      src={allImgPaths.dropdownArrow}
                      alt=""
                    />
                  </div>
                </div>
                {isPatentOpen(p.patentId) && (
                  <div className="px-2 py-1.5">
                    {p.parts.map((part: any) => (
                      <div
                        key={part.num}
                        className="flex items-start gap-x-3 py-1 border-b border-[#F2F4F7] last:border-b-0"
                      >
                        <span className="text-sm font-interMedium text-[#1751D0] min-w-[44px] shrink-0">
                          {part.num}
                        </span>
                        <span className="text-sm font-interRegular text-[#101828]">
                          {part.name}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {!showParts && (
        <div className="px-5">
          <p className="text-sm font-interMedium text-[#2D2B32] pb-2">
            Viewing for Citation #{" "}
          </p>
          <div className="modal-select-box fete-settings-modals mb-6">
            <CustomSelect
              options={citationOptions}
              onChange={handleCitationChange}
              extendedOption={false}
              selectedValue={selectedCitation}
              // defaultValue={citationOptions.length > 0 && citationOptions[0]}
            />
          </div>
        </div>
        )}

        {!showParts && (
        <div
          className={`overflow-y-auto px-5 ${
            hasParts ? "h-[calc(100vh_-_250px)]" : "h-[calc(100vh_-_197px)]"
          }`}
        >
          {citationAccordionData.map((item, index) => (
            <div
              className={`mb-2 last:mb-0 ${
                index === activeCitation ? "" : "accordion-hide"
              }`}
              key={index}
            >
              <div
                className="accordion-heading bg-[#F2F5FF] rounded-sm py-2 pl-2 pr-3 flex items-center justify-between cursor-pointer"
                onClick={() => handleCitationClick(index)}
              >
                <span className="text-sm font-interMedium text-[#101828]">
                  {item.label}
                </span>
                <img
                  className="accordion-arrow cursor-pointer hover:bg-[#f2f4f7] rounded w-5"
                  src={allImgPaths.dropdownArrow}
                  alt=""
                />
              </div>
              <div className="accordion-content px-2">
                {item.file_url ? (
                  <p
                    className="text-sm font-interMedium text-[#1A75FF] pb-2.5 pt-2.5 cursor-pointer hover:text-blue-700 hover:underline "
                    onClick={() => window.open(item.file_url, "_blank")}
                  >
                    {item.primary_heading}
                  </p>
                ) : (
                  <p className="text-sm font-interMedium text-[#101828] pb-2.5 pt-2.5">
                    {item.primary_heading}
                  </p>
                )}
                <p className="text-sm font-interRegular text-[#000000] pb-2">
                  {item.desc}
                </p>
                {/* <img className='mx-auto' src={allImgPaths.dummyImg} alt="" /> */}
              </div>
            </div>
          ))}
        </div>
        )}
      </div>
    </div>
  );
};

export default CitationsSidebar;
